import { CommunityAnnouncements } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { CommunityAnnouncement } from 'lib/client/src/platformapi.schemas';
import { useCallback, useMemo, useState, useContext } from 'react';
import { CreateCommunityAnnouncementModal } from './CreateCommunityAnnouncementModal';
import { DeleteCommunityAnnouncementModal } from './DeleteCommunityAnnouncement';
import { UpdateCommunityAnnouncementModal } from './UpdateCommunityAnnouncementModal';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';

const CommunityAnnouncementsAdmin = () => {
  return (
    <AdminListPageWrapper>
      <CommunityAnnouncementsAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<CommunityAnnouncement>();

const CommunityAnnouncementsAdminContent = () => {
  const { adminQueryOptions, setPagination } = useContext(AdminTableContext);
  const [isCreateCommunityAnnouncementOpen, setIsCreateCommunityAnnouncementOpen] = useState(false);
  const [openCommunityAnnouncement, setOpenCommunityAnnouncement] = useState<
    CommunityAnnouncement | undefined
  >();
  const [communityAnnouncementToDelete, setCommunityAnnouncementToDelete] = useState<
    number | undefined
  >(undefined);

  const { data: communityAnnouncementCount = adminQueryOptions.page } =
    CommunityAnnouncements.useCountCommunityAnnouncements();

  const {
    data: communityAnnouncements = [],
    isError,
    isLoading,
    isSuccess,
  } = CommunityAnnouncements.useGetCommunityAnnouncements(
    {
      page: adminQueryOptions.page,
      limit: adminQueryOptions.limit,
      sort: adminQueryOptions.sort,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const openCommunityAnnouncementModal = useCallback(
    (communityAnnouncementId: number) => {
      setOpenCommunityAnnouncement(
        communityAnnouncements.find(
          (communityAnnouncement) => communityAnnouncement.id === communityAnnouncementId,
        ),
      );
    },
    [communityAnnouncements],
  );

  const openDeleteCommunityAnnouncementModal = (jobPostingId: number) => {
    setCommunityAnnouncementToDelete(jobPostingId);
  };

  const closeCommunityAnnouncementModal = () => {
    setOpenCommunityAnnouncement(undefined);
    setIsCreateCommunityAnnouncementOpen(false);
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (id) => id.getValue(),
          header: () => <span>Id</span>,
          size: 50,
          enableSorting: true,
        }),
        columnHelper.accessor('title', {
          cell: (title) => title.getValue(),
          header: () => <span>Title</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('description', {
          cell: (description) => {
            let descriptionText = description.getValue();
            if (descriptionText.length > 150) {
              descriptionText = description.getValue().substring(0, 150) + '...';
            }
            return descriptionText;
          },
          header: () => 'Description',
          size: 350,
          maxSize: 500,
          enableSorting: false,
        }),
        columnHelper.accessor('callToAction', {
          cell: (cta) => cta.getValue(),
          header: () => <span>Call to Action</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('link', {
          cell: (info) => <div className='text-wrap w-[150px] break-words'>{info.getValue()}</div>,
          header: () => <span>Link</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('type', {
          cell: (type) => type.getValue(),
          header: () => <span>Type</span>,
          size: 100,
          enableSorting: true,
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          header: () => <span>Created At</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.display({
          id: 'actions',
          header: () => <span>Actions</span>,
          size: 25,
          cell: ({ row }) => (
            <AdminActionsContainer
              actions={[
                {
                  text: 'Edit',
                  type: 'button',
                  onPress: () => openCommunityAnnouncementModal(row.original.id),
                },
                {
                  text: 'Delete',
                  type: 'button',
                  onPress: () => openDeleteCommunityAnnouncementModal(row.original.id),
                  linkColor: 'red',
                },
              ]}
            />
          ),
        }),
      ] as ColumnDef<CommunityAnnouncement, unknown>[],
    [openCommunityAnnouncementModal],
  );

  return (
    <>
      <AdminTable
        data={communityAnnouncements}
        columns={columns}
        filteredCount={communityAnnouncementCount}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        tableActions={[
          {
            callback: () => setIsCreateCommunityAnnouncementOpen(true),
            text: 'Create community announcement',
          },
        ]}
      />
      <CreateCommunityAnnouncementModal
        isOpen={isCreateCommunityAnnouncementOpen}
        onClose={closeCommunityAnnouncementModal}
        resetPagination={() =>
          setPagination({ pageIndex: adminQueryOptions.page, pageSize: adminQueryOptions.limit })
        }
        limit={adminQueryOptions.limit}
      />
      {communityAnnouncementToDelete && (
        <DeleteCommunityAnnouncementModal
          isOpen={!!communityAnnouncementToDelete}
          onClose={() => setCommunityAnnouncementToDelete(undefined)}
          id={communityAnnouncementToDelete}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
      {openCommunityAnnouncement && (
        <UpdateCommunityAnnouncementModal
          communityAnnouncement={openCommunityAnnouncement}
          isOpen={!!openCommunityAnnouncement}
          onClose={closeCommunityAnnouncementModal}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
    </>
  );
};

export default CommunityAnnouncementsAdmin;
